import { useState } from "react";
import * as Sentry from "@sentry/react";
import { AnimatePresence, motion } from "framer-motion";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { isTRPCClientError } from "@trpc/react-query";
import { Component as ErrorPage } from "./pages/error.js";
import { Component as Home } from "./pages/home.js";
import { Component as Page } from "./pages/page.js";
import { Component as Profile } from "./pages/profile/index.js";
import trpc from "./utils/trpc";
import { Component as CourseNotInitialized } from "./pages/course-not-initialized.js";
import { Component as CourseInitialized } from "./pages/course-initialized.js";
import { Component as Quiz } from "./pages/quiz.js";
import { Component as AssessmentWelcome } from "./pages/assessment-welcome.js";
import { Component as AssessmentQuiz } from "./pages/assessment-quiz.js";
import LearningItemView from "./components/learning-item-view.js";
import MainLayout from "./components/main-layout.js";
import AppLoadingScreen from "./components/app-loading-screen.js";

function AppErrorScreen({ error }: { error: unknown }) {
  const isUnauthorized = isTRPCClientError(error) && error.data?.code === "UNAUTHORIZED";
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ecru px-6 py-12 text-center">
      {isUnauthorized ? (
        <>
          <h1 className="text-2xl font-semibold text-midnight">Session expired</h1>
          <p className="text-midnight/70">Please relaunch the application.</p>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-semibold text-midnight">Something went wrong</h1>
          <p className="text-midnight/70">An unexpected error occurred. Please try refreshing the page.</p>
        </>
      )}
    </div>
  );
}

function RootLayout() {
  // Preload essential data and show loading screen until it's ready
  const { isLoading: isSessionLoading, error: sessionError } = trpc.session.get.useQuery(undefined, {
    staleTime: Infinity,
  });
  const { isLoading: isModulesLoading, error: modulesError } = trpc.course.getModules.useQuery(undefined, {
    staleTime: Infinity,
  });
  const { isLoading: isProgressionLoading, error: progressionError } = trpc.progression.get.useQuery(undefined, {
    staleTime: Infinity,
  });
  const { isLoading: isAssessmentInfoLoading, error: assessmentInfoError } = trpc.course.getAssessmentInfo.useQuery(
    undefined,
    { staleTime: Infinity },
  );
  const { isLoading: isCourseSettingsLoading, error: courseSettingsError } = trpc.course.getSettings.useQuery(
    undefined,
    { staleTime: Infinity },
  );

  const isAppLoading =
    isSessionLoading || isModulesLoading || isProgressionLoading || isAssessmentInfoLoading || isCourseSettingsLoading;
  const appError = sessionError ?? modulesError ?? progressionError ?? assessmentInfoError ?? courseSettingsError;

  return (
    <AnimatePresence mode="wait">
      {isAppLoading ? (
        <AppLoadingScreen key="app-loading" />
      ) : appError ? (
        <AppErrorScreen key="app-error" error={appError} />
      ) : (
        <motion.div
          key="app-content"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}>
          <Outlet />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CourseStatusGate() {
  const [adminContinued, setAdminContinued] = useState(false);
  const {
    data: statusData,
    isLoading: isStatusLoading,
    error: statusError,
  } = trpc.course.status.useQuery(undefined, { staleTime: Infinity });
  const { data: sessionData, isLoading: isSessionLoading } = trpc.session.get.useQuery(undefined, {
    staleTime: Infinity,
  });

  const isLoading = isStatusLoading || isSessionLoading;
  const isAdmin = !!sessionData?.userInfo.roles.includes(
    "http://purl.imsglobal.org/vocab/lis/v2/institution/person#Administrator",
  );

  if (isLoading) {
    return <AppLoadingScreen />;
  }

  if (statusError) {
    return <AppErrorScreen error={statusError} />;
  }

  if (statusData && !statusData.initialized) {
    return <CourseNotInitialized />;
  }

  if (statusData?.initialized && isAdmin && !adminContinued) {
    return <CourseInitialized onContinue={() => setAdminContinued(true)} />;
  }

  return <RootLayout />;
}

function JourneyGate() {
  const { data: courseSettings } = trpc.course.getSettings.useQuery(undefined, { staleTime: Infinity });

  if (courseSettings?.journeyTabEnabled === false) {
    return <Navigate to="/profile" replace />;
  }

  return <Outlet />;
}

function AssessmentGate() {
  const { data } = trpc.course.getAssessmentInfo.useQuery(undefined, { staleTime: Infinity });

  if (data && data.required && !data.completed) {
    return <Navigate to="/assessment/welcome" replace />;
  }

  return <Outlet />;
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    // Gate all routes behind course initialization status
    element: <CourseStatusGate />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/assessment/welcome",
        Component: AssessmentWelcome,
      },
      {
        path: "/assessment",
        Component: AssessmentQuiz,
      },
      {
        // Gate main app routes behind assessment completion check
        element: <AssessmentGate />,
        children: [
          {
            element: <MainLayout />, // Wrap routes with main layout
            children: [
              {
                element: <JourneyGate />,
                children: [{ path: "/", Component: Home }],
              },
              {
                path: "/profile",
                Component: Profile,
              },
              {
                element: <LearningItemView />,
                children: [
                  {
                    path: "/pages/:pageId",
                    Component: Page,
                  },
                  {
                    path: "/quizzes/:quizId",
                    Component: Quiz,
                  },
                ],
              },
              { path: "*", element: <Navigate to="/" replace /> }, // Fallback to home for unknown routes
            ],
          },
        ],
      },
    ],
  },
]);
